import * as React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui/primitives/button";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/primitives/select";
import { authIntegration } from "@/lib/api/client";
import { useAuthStore } from "@/store/auth.store";
import { useTenantStore } from "@/store/tenant.store";
import { OtpCountdownHint } from "@/components/auth/OtpCountdownHint";
import { useOtpCountdown } from "@/hooks/useOtpCountdown.js";

const EXPIRATION_OPTIONS = [
  { value: "30d", labelKey: "oneMonth" },
  { value: "90d", labelKey: "threeMonths" },
  { value: "180d", labelKey: "sixMonths" },
  { value: "365d", labelKey: "oneYear" },
];

/**
 * Self-service security: password change (OTP only when MFA on) + expiration period.
 * MFA enable/disable is admin-only (User Management).
 */
export function SecuritySettingsPanel() {
  const { t } = useTranslation("header");
  const user = useAuthStore((s) => s.user);
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [channels, setChannels] = React.useState({ email: false, sms: false });

  const [pwdStep, setPwdStep] = React.useState("form");
  const [pwdChannel, setPwdChannel] = React.useState("email");
  const [pwdMasked, setPwdMasked] = React.useState("");
  const [pwdCode, setPwdCode] = React.useState("");
  const [otpMeta, setOtpMeta] = React.useState({ expiresInSec: 300, resendCooldownSec: 60, maxAttempts: 5 });
  const [issuedAt, setIssuedAt] = React.useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = React.useState(null);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordExpirationPeriod, setPasswordExpirationPeriod] = React.useState(
    user?.security?.passwordExpirationPeriod || "90d",
  );
  const [busy, setBusy] = React.useState(false);

  const mfaEnabled = Boolean(user?.security?.mfaEnabled);

  const countdown = useOtpCountdown({
    issuedAt: pwdStep === "code" ? issuedAt : null,
    expiresInSec: otpMeta.expiresInSec,
    resendCooldownSec: otpMeta.resendCooldownSec,
  });

  React.useEffect(() => {
    authIntegration.getVerificationChannels(selectedTenantId).then((r) => {
      const data = r.data || { email: false, sms: false };
      setChannels(data);
      if (data.email) setPwdChannel("email");
      else if (data.sms) setPwdChannel("sms");
    });
  }, [selectedTenantId]);

  React.useEffect(() => {
    if (user?.security?.passwordExpirationPeriod) {
      setPasswordExpirationPeriod(user.security.passwordExpirationPeriod);
    }
  }, [user?.security?.passwordExpirationPeriod]);

  const channelChoices = [
    channels.email && { value: "email", label: t("settings.security.channelEmail", { defaultValue: "Email" }) },
    channels.sms && { value: "sms", label: t("settings.security.channelSms", { defaultValue: "SMS" }) },
  ].filter(Boolean);

  const passwordFormValid =
    Boolean(currentPassword)
    && newPassword.length >= 6
    && newPassword === confirmPassword
    && Boolean(passwordExpirationPeriod);

  const resetPasswordFields = () => {
    setPwdStep("form");
    setPwdCode("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setIssuedAt(null);
    setAttemptsRemaining(null);
  };

  const startPasswordChangeWithOtp = async () => {
    if (!passwordFormValid) return;
    setBusy(true);
    try {
      const result = await authIntegration.requestPasswordChangeOtp({ channel: pwdChannel });
      if (result.success) {
        setPwdStep("code");
        setPwdMasked(result.data?.maskedDestination || "");
        if (result.data?.channel) setPwdChannel(result.data.channel);
        setOtpMeta({
          expiresInSec: Number(result.data?.expiresInSec) || 300,
          resendCooldownSec: Number(result.data?.resendCooldownSec) || 60,
          maxAttempts: Number(result.data?.maxAttempts) || 5,
        });
        setIssuedAt(Date.now());
        setAttemptsRemaining(null);
        setPwdCode("");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitPasswordChange = async () => {
    if (!passwordFormValid) return;
    if (mfaEnabled && pwdStep === "code" && !pwdCode.trim()) return;
    setBusy(true);
    try {
      const payload = {
        currentPassword,
        newPassword,
        confirmNewPassword: confirmPassword,
        passwordExpirationPeriod,
      };
      if (mfaEnabled) {
        payload.code = pwdCode.trim();
        payload.channel = pwdChannel;
      }
      const result = await authIntegration.confirmPasswordChange(payload);
      if (result.success) {
        resetPasswordFields();
      } else if (typeof result.attemptsRemaining === "number") {
        setAttemptsRemaining(result.attemptsRemaining);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 py-2">
      <section className="space-y-3 rounded-[var(--ds-radius-surface)] border border-border bg-card p-4">
        <h3 className="text-base font-semibold">
          {t("settings.security.mfaSection", { defaultValue: "Two-factor authentication" })}
        </h3>
        <p className="text-sm text-muted-foreground">
          {mfaEnabled
            ? t("settings.security.mfaOnAdmin", {
              defaultValue: "MFA is enabled for your account. An administrator can change this in User Management.",
            })
            : t("settings.security.mfaOffAdmin", {
              defaultValue: "MFA is disabled for your account. An administrator can enable it in User Management.",
            })}
        </p>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">
          {t("settings.security.passwordSection", { defaultValue: "Change password" })}
        </h3>
        <div className="space-y-3 rounded-[var(--ds-radius-surface)] border border-border bg-card p-4">
          <div className="space-y-1">
            <Label>{t("settings.security.currentPassword", { defaultValue: "Current password" })}</Label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("settings.security.newPassword", { defaultValue: "New password" })}</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("settings.security.confirmPassword", { defaultValue: "Confirm new password" })}</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("settings.security.passwordExpiration", { defaultValue: "Password expiration" })}</Label>
            <Select value={passwordExpirationPeriod} onValueChange={setPasswordExpirationPeriod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPIRATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(`settings.security.expiration.${opt.labelKey}`, { defaultValue: opt.value })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mfaEnabled && pwdStep === "form" && (
            <>
              {channelChoices.length > 1 && (
                <div className="space-y-1">
                  <Label>{t("settings.security.channel", { defaultValue: "Channel" })}</Label>
                  <Select value={pwdChannel} onValueChange={setPwdChannel}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {channelChoices.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                type="button"
                disabled={busy || !passwordFormValid || !channelChoices.length}
                onClick={startPasswordChangeWithOtp}
              >
                {t("settings.security.sendPasswordCode", { defaultValue: "Send verification code" })}
              </Button>
            </>
          )}

          {mfaEnabled && pwdStep === "code" && (
            <>
              <p className="text-sm text-muted-foreground">
                {t("settings.security.codeSentTo", {
                  defaultValue: "Code sent to {{destination}}",
                  destination: pwdMasked || "…",
                })}
              </p>
              <div className="space-y-1">
                <Label>{t("settings.security.code", { defaultValue: "Verification code" })}</Label>
                <Input value={pwdCode} onChange={(e) => setPwdCode(e.target.value)} maxLength={8} />
                <OtpCountdownHint
                  ns="header"
                  active={countdown.active}
                  expired={countdown.expired}
                  expiresLabel={countdown.expiresLabel}
                  canResend={countdown.canResend}
                  resendInSec={countdown.resendInSec}
                  attemptsRemaining={attemptsRemaining}
                  maxAttempts={otpMeta.maxAttempts}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={busy || !passwordFormValid || !pwdCode.trim() || countdown.expired}
                  onClick={submitPasswordChange}
                >
                  {t("settings.security.updatePassword", { defaultValue: "Update password" })}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || !countdown.canResend}
                  onClick={startPasswordChangeWithOtp}
                >
                  {t("settings.security.resendCode", { defaultValue: "Resend code" })}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPwdStep("form");
                    setPwdCode("");
                    setIssuedAt(null);
                    setAttemptsRemaining(null);
                  }}
                >
                  {t("settings.security.cancel", { defaultValue: "Cancel" })}
                </Button>
              </div>
            </>
          )}

          {!mfaEnabled && (
            <Button type="button" disabled={busy || !passwordFormValid} onClick={submitPasswordChange}>
              {t("settings.security.updatePassword", { defaultValue: "Update password" })}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
